import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Collapse,
  Pagination,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  equalFitCols,
  fitTruncateSx,
  fitChipSx,
  fitHeaderLabel,
} from '../utils/fitTable';
import {
  chartAxisEuroTick,
  chartYearTotals,
  flattenYearStack,
  householdChartClickName,
  householdXAxisFromValues,
  HOUSEHOLD_CHART_BAR_BACKGROUND,
  HOUSEHOLD_CHART_HOVER_CURSOR,
  rowYearShadeFill,
  yearLegendFill,
  yearLegendText,
} from '../utils/personalFinancesChart';
import {
  householdBarCountLabel,
  householdChartYAxisTick,
  HOUSEHOLD_CHART_ROW_HEIGHT,
  HOUSEHOLD_CHART_Y_AXIS_WIDTH,
} from '../utils/personalFinancesChartLabels';
import { householdChartBarOpacity } from '../utils/cashflowColors';
import { PersonalFinancesChartTooltip } from '../utils/personalFinancesChartTooltip';
import {
  buildGlCategoryChartRows,
  GL_YEAR_ALL,
  isAllGlYear,
  type GlCategoryYearRow,
  yearsForGlCategoryTables,
} from '../utils/glCategoryChart';

interface GLTransaction {
  id: number;
  name: string;
  date: string | null;
  number: string | null;
  reference: string | null;
  source: string | null;
  annotation: string | null;
  debit: number;
  credit: number;
  balance: number;
  category_heading: string | null;
  tax_return_year: string | null;
}

interface GroupedAccount {
  accountName: string;
  transactions: GLTransaction[];
  openingBalance: number;
  closingBalance: number;
  netChange: number;
  maxAmount: number;
  minAmount: number;
  avgAmount: number;
  typeBreakdown: Record<string, number>;
}

const GL_CHART_FILL = '#1565C0';

const GeneralLedger: React.FC = () => {
  const [transactions, setTransactions] = useState<GLTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  const [year, setYear] = useState<string>(GL_YEAR_ALL);
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [chartRows, setChartRows] = useState<GlCategoryYearRow[]>([]);
  const [chartYears, setChartYears] = useState<string[]>([]);
  const [chartOpen, setChartOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<number | 'ALL'>(100);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadYear, setUploadYear] = useState<string>(new Date().getFullYear().toString());
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [uploadStep, setUploadStep] = useState(0); // 0 = file selection, 1 = year selection
  const { user } = useAuth();

  useEffect(() => {
    fetchFilterOptions();
    fetchChartSummary();
  }, []);

  useEffect(() => {
    if (!selectedCategory) {
      setTransactions([]);
      setTotalTransactions(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    fetchTransactions();
  }, [year, currentPage, rowsPerPage, selectedCategory]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await api.get('/api/tax-returns/gl-transactions/filter-options', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const years = Array.isArray(response.data.years) ? response.data.years : [];
      setAvailableYears(years);
      setYear((prev) => (isAllGlYear(prev) || years.includes(prev) ? prev : GL_YEAR_ALL));
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchChartSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      setChartLoading(true);
      const response = await api.get('/api/tax-returns/gl-transactions/category-year-summary', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const years = Array.isArray(response.data.years) ? response.data.years : [];
      const categories = Array.isArray(response.data.categories) ? response.data.categories : [];
      setChartYears(years);
      setChartRows(categories);
      setYear((prev) => (isAllGlYear(prev) || years.includes(prev) ? prev : GL_YEAR_ALL));
    } catch (err) {
      console.error('Error fetching category year summary:', err);
    } finally {
      setChartLoading(false);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        return;
      }

      const params = new URLSearchParams({
        page: isAllGlYear(year) ? '1' : currentPage.toString(),
        sort_field: 'date',
        sort_direction: 'desc'
      });
      if (!isAllGlYear(year)) {
        params.append('year', year);
      }

      if (isAllGlYear(year) || rowsPerPage === 'ALL') {
        params.append('per_page', '999999');
      } else {
        params.append('per_page', rowsPerPage.toString());
      }

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedCategory) {
        params.append('category_heading', selectedCategory);
      }

      const response = await api.get(`/api/tax-returns/gl-transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const fetchedTransactions = response.data.transactions || [];
      setTransactions(fetchedTransactions);
      setTotalTransactions(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.pages || 1);

      const tableYears = yearsForGlCategoryTables(
        year,
        fetchedTransactions.map((txn: GLTransaction) => txn.tax_return_year),
        availableYears
      );
      const expanded: Record<string, boolean> = {};
      tableYears.forEach((tableYear) => {
        const yearTxns = isAllGlYear(year)
          ? fetchedTransactions.filter((txn: GLTransaction) => String(txn.tax_return_year || '') === tableYear)
          : fetchedTransactions;
        Object.keys(groupTransactionsByAccount(yearTxns)).forEach((accountKey) => {
          expanded[isAllGlYear(year) ? `${tableYear}::${accountKey}` : accountKey] = true;
        });
      });
      setExpandedAccounts(expanded);
    } catch (err: any) {
      console.error('Error fetching GL transactions:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError('Failed to fetch GL transactions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const groupTransactionsByAccount = (txns: GLTransaction[]): Record<string, GroupedAccount> => {
    const grouped: Record<string, GroupedAccount> = {};

    txns.forEach(txn => {
      const accountKey = txn.category_heading || txn.name || 'Uncategorized';

      if (!grouped[accountKey]) {
        grouped[accountKey] = {
          accountName: accountKey,
          transactions: [],
          openingBalance: 0,
          closingBalance: 0,
          netChange: 0,
          maxAmount: 0,
          minAmount: 0,
          avgAmount: 0,
          typeBreakdown: {}
        };
      }

      grouped[accountKey].transactions.push(txn);

      // Calculate amounts
      const amount = Math.abs(txn.debit || txn.credit || 0);
      if (amount > 0) {
        if (grouped[accountKey].maxAmount === 0 || amount > grouped[accountKey].maxAmount) {
          grouped[accountKey].maxAmount = amount;
        }
        if (grouped[accountKey].minAmount === 0 || amount < grouped[accountKey].minAmount) {
          grouped[accountKey].minAmount = amount;
        }
      }

      // Track transaction types
      const source = txn.source || 'UNKNOWN';
      grouped[accountKey].typeBreakdown[source] = (grouped[accountKey].typeBreakdown[source] || 0) + 1;
    });

    // Calculate summary statistics for each account
    Object.keys(grouped).forEach(key => {
      const account = grouped[key];
      const amounts = account.transactions
        .map(t => Math.abs(t.debit || t.credit || 0))
        .filter(a => a > 0);

      if (amounts.length > 0) {
        account.maxAmount = Math.max(...amounts);
        account.minAmount = Math.min(...amounts);
        account.avgAmount = amounts.reduce((a, b) => a + b, 0) / amounts.length;
      }

      // Calculate opening, change, and closing
      let opening = 0;
      let change = 0;

      account.transactions.forEach(txn => {
        if (txn.reference === 'Opening') {
          opening = txn.balance || 0;
        } else if (txn.reference === 'Change') {
          change = txn.balance || 0;
        } else if (txn.reference === 'Close') {
          account.closingBalance = txn.balance || 0;
        } else {
          change += (txn.debit || 0) - (txn.credit || 0);
        }
      });

      account.openingBalance = opening;
      account.netChange = change;
      if (account.closingBalance === 0) {
        account.closingBalance = opening + change;
      }
    });

    return grouped;
  };

  const tableYearBlocks = useMemo(() => {
    const tableYears = yearsForGlCategoryTables(
      year,
      transactions.map((txn) => txn.tax_return_year),
      availableYears.length ? availableYears : chartYears
    );
    return tableYears
      .map((tableYear) => {
        const yearTxns = isAllGlYear(year)
          ? transactions.filter((txn) => String(txn.tax_return_year || '') === tableYear)
          : transactions;
        return { tableYear, grouped: groupTransactionsByAccount(yearTxns) };
      })
      .filter((block) => Object.keys(block.grouped).length > 0);
  }, [year, transactions, availableYears, chartYears]);

  const accountTableKey = (tableYear: string, accountKey: string) =>
    isAllGlYear(year) ? `${tableYear}::${accountKey}` : accountKey;

  const toggleAccount = (accountKey: string) => {
    setExpandedAccounts(prev => ({
      ...prev,
      [accountKey]: !prev[accountKey]
    }));
  };

  const expandAll = () => {
    const expanded: Record<string, boolean> = {};
    tableYearBlocks.forEach((block) => {
      Object.keys(block.grouped).forEach((accountKey) => {
        expanded[accountTableKey(block.tableYear, accountKey)] = true;
      });
    });
    setExpandedAccounts(expanded);
  };

  const collapseAll = () => {
    setExpandedAccounts({});
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const plotRows = useMemo(() => buildGlCategoryChartRows(chartRows), [chartRows]);
  const chartYearStackKeys = useMemo(
    () => [...chartYears].filter((stackYear) => /^\d{4}$/.test(stackYear)).sort(),
    [chartYears]
  );
  const chartPlotData = useMemo(
    () => plotRows.map((row) => flattenYearStack(row, chartYearStackKeys)),
    [plotRows, chartYearStackKeys]
  );
  const chartYearAmountTotals = useMemo(
    () => chartYearTotals(plotRows, chartYearStackKeys),
    [plotRows, chartYearStackKeys]
  );
  const chartXAxis = useMemo(
    () => householdXAxisFromValues(plotRows.map((row) => row.value)),
    [plotRows]
  );

  const handleChartNameClick = (name: string) => {
    const next = String(name || '').trim();
    if (!next) return;
    setSelectedCategory(next);
    setCurrentPage(1);
  };

  const clearCategoryFilter = () => {
    setSelectedCategory(null);
    setCurrentPage(1);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch {
      return 'N/A';
    }
  };

  const getTransactionTypeColor = (source: string | null): string => {
    if (!source) return '#grey';
    switch (source) {
      case 'PJ': return '#1976d2'; // Blue
      case 'AJ': return '#2e7d32'; // Green
      case 'AP': return '#ed6c02'; // Orange
      case 'SE': return '#9c27b0'; // Purple
      case 'CD': return '#d32f2f'; // Red
      case 'PL': return '#0288d1'; // Light Blue
      default: return '#757575'; // Grey
    }
  };

  const getTransactionTypeLabel = (source: string | null): string => {
    if (!source) return 'N/A';
    if (source === 'OPENING') return 'OPENING';
    if (source === 'CHANGE') return 'CHANGE';
    if (source === 'CLOSING') return 'CLOSING';
    return source;
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      alert('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in again');
        return;
      }

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('year', uploadYear);

      const response = await api.post('/api/tax-returns/upload', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('File uploaded successfully!');
      setShowUpload(false);
      setUploadStep(0);
      setUploadFile(null);
      fetchFilterOptions();
      fetchChartSummary();
      if (selectedCategory) {
        fetchTransactions();
      }
    } catch (err: any) {
      console.error('Error uploading file:', err);
      alert(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL General Ledger data? This action cannot be undone.')) {
      return;
    }

    if (!window.confirm('This will permanently delete all uploaded GL files and transactions. Are you absolutely sure?')) {
      return;
    }

    setClearing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in again');
        return;
      }

      const response = await api.delete('/api/tax-returns/clear-all', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      alert(`Successfully cleared all GL data!\nDeleted ${response.data.deleted_transactions} transactions and ${response.data.deleted_tax_returns} tax returns.`);
      setTransactions([]);
      setExpandedAccounts({});
      setTotalTransactions(0);
      setTotalPages(1);
      setSelectedCategory(null);
      setYear(GL_YEAR_ALL);
      setChartRows([]);
      setChartYears([]);
      fetchFilterOptions();
      fetchChartSummary();
    } catch (err: any) {
      console.error('Error clearing GL data:', err);
      alert(err.response?.data?.error || 'Failed to clear GL data');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          General Ledger Transactions
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => {
              console.log('Upload button clicked, setting showUpload to true');
              setShowUpload(true);
            }}
            sx={{ mr: 2 }}
          >
            Upload GL File
          </Button>
          {(user?.role === 'admin' || user?.role === 'owner') && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleClearAll}
              disabled={clearing}
              sx={{ mr: 2 }}
            >
              {clearing ? 'Clearing...' : 'Clear All GL Data'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchFilterOptions();
              fetchChartSummary();
              if (selectedCategory) {
                fetchTransactions();
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Upload Modal */}
      <Dialog
        open={showUpload}
        onClose={() => {
          console.log('Dialog onClose called, uploading:', uploading);
          if (!uploading) {
            setShowUpload(false);
            setUploadStep(0);
            setUploadFile(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={uploading}
        aria-labelledby="upload-dialog-title"
        aria-describedby="upload-dialog-description"
      >
        <DialogTitle id="upload-dialog-title">
          Upload General Ledger File
        </DialogTitle>
        <DialogContent id="upload-dialog-description" dividers>
          <Stepper activeStep={uploadStep} sx={{ mt: 2, mb: 4 }}>
            <Step>
              <StepLabel>Select File</StepLabel>
            </Step>
            <Step>
              <StepLabel>Select Year</StepLabel>
            </Step>
          </Stepper>

          {uploadStep === 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" gutterBottom>
                Choose a General Ledger file to upload (CSV, Excel, or PDF)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PDF files work when they contain selectable text (the usual accountant export). Scanned image PDFs cannot be read — use the Excel or CSV instead.
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,.pdf,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setUploadFile(file);
                    if (file) {
                      // Auto-advance to next step when file is selected
                      setTimeout(() => setUploadStep(1), 300);
                    }
                  }}
                  style={{ display: 'none' }}
                  id="upload-file"
                />
                <label htmlFor="upload-file">
                  <Button variant="contained" component="span" size="large" startIcon={<UploadIcon />}>
                    Choose File
                  </Button>
                </label>
                {uploadFile && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, width: '100%' }}>
                    <Typography variant="body2" color="text.secondary">
                      Selected file:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(uploadFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {uploadStep === 1 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" gutterBottom>
                Select the year for this General Ledger file
              </Typography>
              {uploadFile && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    File: {uploadFile.name}
                  </Typography>
                </Box>
              )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={uploadYear}
                  label="Year"
                  onChange={(e) => setUploadYear(e.target.value)}
                >
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <MenuItem key={y} value={y.toString()}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUpload(false);
              setUploadStep(0);
              setUploadFile(null);
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          {uploadStep === 1 && (
            <Button
              onClick={() => setUploadStep(0)}
              disabled={uploading}
            >
              Back
            </Button>
          )}
          {uploadStep === 0 && uploadFile && (
            <Button
              variant="contained"
              onClick={() => setUploadStep(1)}
            >
              Next
            </Button>
          )}
          {uploadStep === 1 && (
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!uploadFile || uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {(chartLoading || plotRows.length > 0) && (
        <Accordion
          expanded={chartOpen}
          onChange={(_, open) => setChartOpen(open)}
          sx={{ mb: 2 }}
          data-testid="gl-category-chart"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Activity by account</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {chartLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }} data-testid="gl-chart-loading">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 1 }}
                  flexWrap="wrap"
                  useFlexGap
                  data-testid="gl-chart-year-legend"
                >
                  <Chip
                    size="small"
                    clickable
                    label="All"
                    color={isAllGlYear(year) ? 'primary' : 'default'}
                    data-testid="gl-chart-year-all"
                    onClick={() => {
                      setYear(GL_YEAR_ALL);
                      setCurrentPage(1);
                    }}
                  />
                  {chartYearStackKeys.map((stackYear) => (
                    <Chip
                      key={stackYear}
                      size="small"
                      clickable
                      label={stackYear}
                      color={year === stackYear ? 'primary' : 'default'}
                      data-testid={`gl-chart-year-${stackYear}`}
                      onClick={() => {
                        setYear(stackYear);
                        setCurrentPage(1);
                      }}
                      sx={
                        year === stackYear
                          ? undefined
                          : {
                              bgcolor: yearLegendFill(stackYear, chartYearAmountTotals),
                              color: yearLegendText(stackYear, chartYearAmountTotals),
                            }
                      }
                    />
                  ))}
                </Stack>
                <Box
                  sx={{
                    width: '100%',
                    height: Math.max(
                      plotRows.length <= 2 ? 168 : 280,
                      plotRows.length * HOUSEHOLD_CHART_ROW_HEIGHT
                    ),
                    cursor: 'pointer',
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartPlotData}
                      layout="vertical"
                      margin={{ top: 8, right: 148, left: 8, bottom: 8 }}
                      style={{ cursor: 'pointer' }}
                      onClick={(state: any, event: any) => {
                        handleChartNameClick(householdChartClickName(state, chartPlotData));
                        event?.stopPropagation?.();
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        tickFormatter={chartAxisEuroTick}
                        domain={chartXAxis.domain}
                        ticks={chartXAxis.ticks}
                        interval={0}
                        allowDecimals={false}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={HOUSEHOLD_CHART_Y_AXIS_WIDTH}
                        interval={0}
                        tick={householdChartYAxisTick(handleChartNameClick, HOUSEHOLD_CHART_Y_AXIS_WIDTH)}
                      />
                      <RechartsTooltip
                        cursor={HOUSEHOLD_CHART_HOVER_CURSOR}
                        content={(props) => (
                          <PersonalFinancesChartTooltip
                            active={props.active}
                            payload={props.payload as any}
                            label={props.label}
                            stackByYear
                            years={chartYearStackKeys}
                            colorFor={(rowName: string, stackYear?: string) => {
                              const row = plotRows.find((item) => item.name === rowName);
                              return stackYear
                                ? rowYearShadeFill(GL_CHART_FILL, stackYear, row?.byYear, chartYearStackKeys)
                                : GL_CHART_FILL;
                            }}
                            formatMoney={(value: number) => formatCurrency(value)}
                          />
                        )}
                      />
                      {chartYearStackKeys.map((stackYear, yearIndex) => (
                        <Bar
                          key={stackYear}
                          dataKey={stackYear}
                          name={stackYear}
                          stackId="years"
                          cursor="pointer"
                          isAnimationActive={false}
                          background={yearIndex === 0 ? HOUSEHOLD_CHART_BAR_BACKGROUND : undefined}
                          onClick={(item: any, _index: number, event: any) => {
                            event?.stopPropagation?.();
                            handleChartNameClick(String(item?.payload?.name || ''));
                          }}
                        >
                          {plotRows.map((row) => (
                            <Cell
                              key={`${row.name}-${stackYear}`}
                              fill={rowYearShadeFill(
                                GL_CHART_FILL,
                                stackYear,
                                row.byYear,
                                chartYearStackKeys
                              )}
                              fillOpacity={householdChartBarOpacity(row.name, selectedCategory)}
                            />
                          ))}
                          {yearIndex === chartYearStackKeys.length - 1 ? (
                            <LabelList
                              dataKey="value"
                              content={householdBarCountLabel(
                                plotRows,
                                (value) => formatCurrency(value),
                                'gl-bar-label'
                              )}
                            />
                          ) : null}
                        </Bar>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {!chartLoading && plotRows.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No General Ledger transactions found. Upload a GL file to see the overview chart.
        </Alert>
      )}

      {!selectedCategory && !chartLoading && plotRows.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Click a category to show its transactions below. All years are shown by default.
        </Alert>
      )}

      {selectedCategory && (
      <>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={year}
                label="Year"
                onChange={(e) => {
                  setYear(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <MenuItem value={GL_YEAR_ALL}>All</MenuItem>
                {availableYears.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setCurrentPage(1);
                  fetchTransactions();
                }
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ flexGrow: 1, maxWidth: 400 }}
            />
            <Button
              variant="outlined"
              onClick={expandAll}
              size="small"
            >
              Expand All
            </Button>
            <Button
              variant="outlined"
              onClick={collapseAll}
              size="small"
            >
              Collapse All
            </Button>
            <Chip
              icon={<ArrowBackIcon />}
              label={`Account: ${selectedCategory}`}
              onDelete={clearCategoryFilter}
              color="primary"
            />
            <Chip
              label={`YEAR: ${isAllGlYear(year) ? 'All' : year}`}
              onDelete={() => {
                setYear(GL_YEAR_ALL);
                setCurrentPage(1);
              }}
              color="primary"
            />
          </Box>
          
          {/* Pagination Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {isAllGlYear(year) || rowsPerPage === 'ALL'
                  ? `Showing all ${totalTransactions} transactions${isAllGlYear(year) ? ' by year' : ''}`
                  : `Showing ${(currentPage - 1) * (rowsPerPage as number) + 1} - ${Math.min(currentPage * (rowsPerPage as number), totalTransactions)} of ${totalTransactions} transactions`
                }
              </Typography>
              {!isAllGlYear(year) && (
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={rowsPerPage === 'ALL' ? 'ALL' : rowsPerPage.toString()}
                  label="Per Page"
                  onChange={(e) => {
                    const value = e.target.value;
                    setRowsPerPage(value === 'ALL' ? 'ALL' : Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <MenuItem value="25">25</MenuItem>
                  <MenuItem value="50">50</MenuItem>
                  <MenuItem value="100">100</MenuItem>
                  <MenuItem value="200">200</MenuItem>
                  <MenuItem value="ALL">ALL</MenuItem>
                </Select>
              </FormControl>
              )}
            </Box>
            {!isAllGlYear(year) && rowsPerPage !== 'ALL' && totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Grouped Accounts */}
          {tableYearBlocks.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No General Ledger transactions found{isAllGlYear(year) ? '' : ` for ${year}`}. Upload a GL file or pick another year.
            </Alert>
          )}
          {tableYearBlocks.map(({ tableYear, grouped }) => (
            <Box key={tableYear} sx={{ mb: isAllGlYear(year) ? 3 : 0 }}>
              {isAllGlYear(year) && (
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {tableYear}
                </Typography>
              )}
              {Object.keys(grouped).map((accountKey) => {
            const account = grouped[accountKey];
            const cardKey = accountTableKey(tableYear, accountKey);
            const isExpanded = expandedAccounts[cardKey] || false;
            const typeBreakdown = Object.entries(account.typeBreakdown)
              .filter(([type]) => type !== 'UNKNOWN' && type !== 'N/A')
              .map(([type, count]) => `${type}: ${count}`)
              .join(', ');

            return (
              <Card key={cardKey} sx={{ mb: 2 }}>
                <CardContent>
                  {/* Account Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      p: 1,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => toggleAccount(cardKey)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AccountBalanceIcon />
                      <Typography variant="h6">{account.accountName}</Typography>
                      {typeBreakdown && (
                        <Chip
                          label={typeBreakdown}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary">
                          Max: {formatCurrency(account.maxAmount)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Avg: {formatCurrency(account.avgAmount)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Min: {formatCurrency(account.minAmount)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">
                          Opening: {formatCurrency(account.openingBalance)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={account.netChange >= 0 ? 'success.main' : 'error.main'}
                        >
                          Change: {account.netChange >= 0 ? '+' : ''}{formatCurrency(account.netChange)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={account.closingBalance >= 0 ? 'text.primary' : 'error.main'}
                        >
                          Closing: {formatCurrency(account.closingBalance)}
                        </Typography>
                      </Box>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                  </Box>

                  {/* Transactions Table */}
                  <Collapse in={isExpanded}>
                    <TableContainer component={Paper} sx={{ ...FIT_TABLE_CONTAINER_SX, mt: 2 }}>
                      <Table size="small" sx={FIT_TABLE_SX}>
                        <FitColgroup
                          cols={equalFitCols(
                            ['id', 'date', 'description', 'reference', 'type', 'amount', 'dc', 'year', 'bankTx'],
                            { flexKey: 'description' }
                          )}
                        />
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>DATE</TableCell>
                            <TableCell>DESCRIPTION</TableCell>
                            <TableCell>REFERENCE</TableCell>
                            <TableCell>TYPE</TableCell>
                            <TableCell align="right">AMOUNT</TableCell>
                            <TableCell>D/C</TableCell>
                            <TableCell>YEAR</TableCell>
                            <TableCell>{fitHeaderLabel('BANK\nTRANSACTION ID')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {account.transactions.map((txn, idx) => {
                            const amount = (txn.debit || 0) - (txn.credit || 0);
                            const isDebit = txn.debit > 0;
                            const typeColor = getTransactionTypeColor(txn.source);
                            const typeLabel = getTransactionTypeLabel(txn.source);

                            return (
                              <TableRow
                                key={idx}
                                sx={{
                                  bgcolor: txn.reference === 'Change' || txn.reference === 'Close'
                                    ? 'action.hover'
                                    : 'transparent'
                                }}
                              >
                                <TableCell>{txn.id || '-'}</TableCell>
                                <TableCell>{formatDate(txn.date)}</TableCell>
                                <TableCell>
                                  <Box component="span" sx={fitTruncateSx} title={String(txn.name || '')}>
                                    {txn.name}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box component="span" sx={fitTruncateSx} title={String(txn.reference || '')}>
                                    {txn.reference || '-'}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={typeLabel}
                                    size="small"
                                    sx={{
                                      ...fitChipSx,
                                      bgcolor: typeColor,
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color: amount >= 0 ? 'success.main' : 'error.main',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {amount >= 0 ? '+' : ''}{formatCurrency(amount)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={isDebit ? 'DEBIT' : 'CREDIT'}
                                    size="small"
                                    color={isDebit ? 'error' : 'success'}
                                    sx={fitChipSx}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip label={txn.tax_return_year || tableYear} size="small" color="primary" sx={fitChipSx} />
                                </TableCell>
                                <TableCell>-</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
            </Box>
          ))}

        </>
      )}
      </>
      )}
    </Container>
  );
};

export default GeneralLedger;

